import {
  default as React,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCurrentUserEmail } from './hooks';
import { isUserAuthorizedForEntity, useSkyStageAdminEmails } from './hooks';

const WELLSKY_EMAIL_RE = /^[^\s@]+@wellsky\.com$/i;

interface OktaUser {
  id: string;
  profile: {
    login: string;
    email: string;
    firstName: string;
    lastName: string;
    displayName?: string;
  };
}

/**
 * AccessTab — entity page tab for managing allowed-emails for a microapp.
 *
 * Only visible and functional for app owners (gated in EntityPage.tsx via `if` prop).
 *
 * Owners can search Okta users by name or email, add or remove addresses, then
 * submit all changes at once. Changes are committed to the argo repo backstage.yaml
 * and the catalog entity is refreshed immediately so the UI stays consistent.
 */
export const AccessTab = () => {
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const currentUserEmail = useCurrentUserEmail();
  const adminEmails = useSkyStageAdminEmails();

  const appName = entity.metadata.name;
  const isOwner = isUserAuthorizedForEntity(
    entity,
    currentUserEmail,
    adminEmails,
  );

  /** Emails that must always stay in the list — owners cannot be removed. */
  const ownerEmails = useMemo(() => {
    const annotations = entity.metadata.annotations || {};
    const raw = annotations['microapp/owners'] || '';
    const createdBy = annotations['microapp/createdBy'] || '';
    const set = new Set<string>();
    raw.split(',').forEach(e => {
      const t = e.trim().toLowerCase();
      if (t) set.add(t);
    });
    if (createdBy.trim()) set.add(createdBy.trim().toLowerCase());
    return set;
  }, [entity.metadata.annotations]);

  const isOwnerEmail = useCallback(
    (email: string) => ownerEmails.has(email.toLowerCase()),
    [ownerEmails],
  );

  // ─── State ───────────────────────────────────────────────────────────────
  /** Emails currently saved (truth from backend). */
  const [loadedEmails, setLoadedEmails] = useState<string[]>([]);
  /** Working copy — modified by add/remove before Submit is clicked. */
  const [pendingEmails, setPendingEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  /** Okta user suggestions shown in the autocomplete dropdown. */
  const [emailOptions, setEmailOptions] = useState<OktaUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // ─── Helpers ─────────────────────────────────────────────────────────────
  const getAuthHeaders = useCallback(async (): Promise<HeadersInit> => {
    const { token } = await identityApi.getCredentials();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }, [identityApi]);

  const backendUrl = config.getString('backend.baseUrl');
  const accessBase = `${backendUrl}/api/microapps-api/access`;

  /** Whether pendingEmails differs from what is saved. */
  const hasChanges =
    JSON.stringify([...pendingEmails].sort()) !==
    JSON.stringify([...loadedEmails].sort());

  // ─── Load emails from catalog entity annotation ───────────────────────────
  const loadEmails = useCallback(async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${accessBase}/${appName}`, {
        headers,
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          (body as { error?: string }).error ||
            `Failed to load access list (${res.status})`,
        );
      }
      const data = (await res.json()) as { emails?: string[] };
      const emails = Array.isArray(data.emails) ? data.emails : [];
      setLoadedEmails(emails);
      setPendingEmails(emails);
    } catch (err: unknown) {
      setFetchError(
        err instanceof Error ? err.message : 'Failed to load access list',
      );
    } finally {
      setIsLoading(false);
    }
  }, [accessBase, appName, getAuthHeaders]);

  useEffect(() => {
    if (isOwner && currentUserEmail) {
      loadEmails();
    }
  }, [isOwner, currentUserEmail, loadEmails]);

  // ─── Search Okta users ───────────────────────────────────────────────────
  const searchOktaUsers = useCallback(
    async (query: string) => {
      setIsSearching(true);
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(
          `${backendUrl}/api/microapps-api/access/users/search?q=${encodeURIComponent(
            query,
          )}`,
          { headers, credentials: 'include' },
        );
        if (!res.ok) {
          setEmailOptions([]);
          return;
        }
        const data = (await res.json()) as { users: OktaUser[] };
        const users = Array.isArray(data.users) ? data.users : [];
        // Filter out users already in the pending list
        setEmailOptions(
          users.filter(
            u => !pendingEmails.includes(u.profile.email.toLowerCase()),
          ),
        );
      } catch {
        setEmailOptions([]);
      } finally {
        setIsSearching(false);
      }
    },
    [backendUrl, getAuthHeaders, pendingEmails],
  );

  // ─── Add email to pending list (manual typed entry) ──────────────────────
  const handleAddEmail = () => {
    const email = newEmail.trim().toLowerCase();
    setEmailError(null);
    setSuccessMsg(null);

    if (!email) return;

    if (!WELLSKY_EMAIL_RE.test(email)) {
      setEmailError('Only @wellsky.com email addresses can be added.');
      return;
    }
    if (pendingEmails.includes(email)) {
      setEmailError('This email is already in the list.');
      return;
    }
    setPendingEmails(prev => [...prev, email]);
    setNewEmail('');
    setEmailOptions([]);
  };

  // ─── Remove email from pending list ─────────────────────────────────────
  const handleRemoveEmail = (email: string) => {
    if (isOwnerEmail(email)) return; // owners are protected — cannot be removed
    setSuccessMsg(null);
    setPendingEmails(prev => prev.filter(e => e !== email));
  };

  // ─── Reset pending list to saved state ──────────────────────────────────
  const handleReset = () => {
    setPendingEmails(loadedEmails);
    setNewEmail('');
    setEmailOptions([]);
    setEmailError(null);
    setSuccessMsg(null);
  };

  // ─── Submit batch changes ────────────────────────────────────────────────
  const handleSubmit = async () => {
    setIsSaving(true);
    setFetchError(null);
    setSuccessMsg(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${accessBase}/${appName}`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ emails: pendingEmails }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          (body as { error?: string }).error ||
            `Failed to update access list (${res.status})`,
        );
      }
      const data = (await res.json()) as { emails?: string[] };
      const saved = Array.isArray(data.emails) ? data.emails : pendingEmails;
      setLoadedEmails(saved);
      setPendingEmails(saved);
      setSuccessMsg('Access list updated successfully.');
      // Trigger a catalog entity refresh so the microapp/allowedUsers annotation
      // is re-ingested from the argo repo. Non-blocking — the optimistic state
      // above already shows the correct emails for the current session.
      catalogApi.refreshEntity(stringifyEntityRef(entity)).catch(() => {});
    } catch (err: unknown) {
      setFetchError(
        err instanceof Error ? err.message : 'Failed to update access list',
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Render: email still resolving ───────────────────────────────────────
  if (!currentUserEmail) {
    return (
      <Box p={3} display="flex" alignItems="center" gap={1}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading…</Typography>
      </Box>
    );
  }

  // ─── Render: non-owner ────────────────────────────────────────────────────
  if (!isOwner) return null;

  // ─── Render: management UI ───────────────────────────────────────────────
  return (
    <Box p={3}>
      <Typography variant="h6" gutterBottom>
        Manage Access
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Manage which <strong>@wellsky.com</strong> users can access this app.
        Search by name or email, add or remove users below, then click{' '}
        <strong>Submit</strong> to save.
      </Alert>

      {fetchError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setFetchError(null)}
        >
          {fetchError}
        </Alert>
      )}

      {successMsg && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccessMsg(null)}
        >
          {successMsg}
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />

      {/* ── Current allowed emails ── */}
      <Typography variant="subtitle1" gutterBottom>
        Allowed Users
      </Typography>

      {isLoading && (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading…</Typography>
        </Box>
      )}

      {!isLoading && pendingEmails.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No users added yet. Use the field below to add @wellsky.com addresses.
        </Typography>
      )}

      {!isLoading && pendingEmails.length > 0 && (
        <Box
          display="flex"
          flexWrap="wrap"
          gap={1}
          mb={2}
          p={1.5}
          border={1}
          borderColor="divider"
          borderRadius={1}
        >
          {pendingEmails.map(email => {
            const isNew = !loadedEmails.includes(email);
            const isProtected = isOwnerEmail(email);
            let chipTitle: string | undefined;
            if (isProtected) {
              chipTitle = 'Owner — cannot be removed';
            } else if (isNew) {
              chipTitle = 'Pending addition';
            }
            return (
              <Chip
                key={email}
                label={email}
                size="small"
                color={isNew ? 'primary' : 'default'}
                variant={isNew ? 'filled' : 'outlined'}
                onDelete={
                  isProtected ? undefined : () => handleRemoveEmail(email)
                }
                title={chipTitle}
              />
            );
          })}
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* ── Add email ── */}
      <Typography variant="subtitle1" gutterBottom>
        Add User
      </Typography>
      <Box
        display="flex"
        gap={1}
        mb={emailError ? 0.5 : 2}
        alignItems="flex-start"
      >
        <Autocomplete
          sx={{ flex: 1 }}
          options={emailOptions}
          getOptionLabel={(option: OktaUser) => {
            const { firstName, lastName, email } = option.profile;
            return `${firstName} ${lastName} (${email})`;
          }}
          isOptionEqualToValue={(o, v) => o.profile.email === v.profile.email}
          inputValue={newEmail}
          onInputChange={(_, val, reason) => {
            if (reason === 'reset') return;
            setNewEmail(val);
            setEmailError(null);
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
            if (val.trim().length >= 2) {
              searchTimerRef.current = setTimeout(() => {
                void searchOktaUsers(val.trim());
              }, 300);
            } else {
              setEmailOptions([]);
            }
          }}
          onChange={(_, user: OktaUser | null) => {
            if (!user) return;
            const email = user.profile.email.toLowerCase();
            setEmailError(null);
            setSuccessMsg(null);
            if (pendingEmails.includes(email)) {
              setEmailError('This email is already in the list.');
            } else {
              setPendingEmails(prev => [...prev, email]);
            }
            setNewEmail('');
            setEmailOptions([]);
          }}
          value={null}
          loading={isSearching}
          filterOptions={x => x}
          noOptionsText={
            newEmail.trim().length < 2
              ? 'Type at least 2 characters to search'
              : 'No users found'
          }
          renderInput={params => (
            <TextField
              {...params}
              label="Search by name or email"
              placeholder="e.g. john or john@wellsky.com"
              variant="outlined"
              size="small"
              error={!!emailError}
              InputLabelProps={{ shrink: true }}
              onKeyDown={e => {
                if (e.key === 'Enter') handleAddEmail();
              }}
              inputProps={{
                ...params.inputProps,
                'aria-label': 'Search Okta users by name or email',
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isSearching && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.profile.email}>
              <Box>
                <Typography variant="body2">
                  {option.profile.firstName} {option.profile.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.profile.email}
                </Typography>
              </Box>
            </li>
          )}
        />
        <Button
          variant="outlined"
          onClick={handleAddEmail}
          disabled={!newEmail.trim()}
          sx={{ alignSelf: 'flex-start', mt: '2px' }}
        >
          Add
        </Button>
      </Box>

      {emailError && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mb: 2, display: 'block' }}
        >
          {emailError}
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />

      {/* ── Submit / Reset ── */}
      {pendingEmails.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          At least one user must remain in the access list.
        </Alert>
      )}
      <Box display="flex" gap={2} alignItems="center">
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!hasChanges || isSaving || pendingEmails.length === 0}
          startIcon={
            isSaving ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {isSaving ? 'Saving…' : 'Submit Changes'}
        </Button>
        <Button
          variant="text"
          onClick={handleReset}
          disabled={!hasChanges || isSaving}
        >
          Reset
        </Button>
        {hasChanges && !isSaving && (
          <Typography variant="caption" color="text.secondary">
            Unsaved changes
          </Typography>
        )}
      </Box>
    </Box>
  );
};
